import { createEntityAdapter, EntityAdapter } from '@ngrx/entity';
import { Action, createReducer, on } from '@ngrx/store';
import { Location } from '../../models/Location';
import * as locationsActions from './locations.actions';
import { LocationsState } from './locations.state';

export const locsAdapter: EntityAdapter<Location> = createEntityAdapter<Location>();
export const locationsInitialState: LocationsState = locsAdapter.getInitialState({
	loading: false,
	loaded: false,
	createdLocationId: null,
	updatedLocationId: null,
	foundLocations: [],
	fromCategory: [],
	locationsWithPinnables: [],
	locationsWithPinnablesLoading: false,
	locationsWithPinnablesLoaded: false,
});

const reducer = createReducer(
	locationsInitialState,
	on(
		locationsActions.getLocations,
		locationsActions.getLocationsFromCategory,
		locationsActions.searchLocations,
		locationsActions.postLocation,
		locationsActions.updateLocation,
		locationsActions.removeLocation,
		(state) => ({ ...state, loading: true, loaded: false, createdLocationId: null, updatedLocationId: null })
	),
	on(locationsActions.getLocationsSuccess, (state, { locations }) => {
		return locsAdapter.setAll(locations, { ...state, loading: false, loaded: true });
	}),
	on(locationsActions.searchLocationsSuccess, (state, { foundLocations }) => {
		return {
			...state,
			loading: false,
			loaded: true,
			foundLocations,
		};
	}),
	on(locationsActions.getLocationsFromCategorySuccess, (state, { locations }) => {
		return {
			...state,
			loading: false,
			loaded: true,
			fromCategory: locations,
		};
	}),
	on(locationsActions.postLocationSuccess, (state, { location }) => {
		return locsAdapter.addOne(location, { ...state, loading: false, loaded: true, createdLocationId: location.id });
	}),
	on(locationsActions.updateLocationSuccess, (state, { location }) => {
		return locsAdapter.upsertOne(location, { ...state, loading: false, loaded: true, updatedLocationId: location.id });
	}),
	on(locationsActions.removeLocationSuccess, (state, { id }) => {
		return locsAdapter.removeOne(+id, { ...state, loading: false, loaded: true });
	}),

	// LocationWithPinnable reducers
	on(locationsActions.fetchLocationsWithPinnables, (state) => ({
		...state,
		locationsWithPinnablesLoading: true,
		locationsWithPinnablesLoaded: false,
	})),
	on(locationsActions.fetchLocationsWithPinnablesSuccess, (state, { locations }) => ({
		...state,
		locationsWithPinnables: locations,
		locationsWithPinnablesLoading: false,
		locationsWithPinnablesLoaded: true,
	})),
	on(locationsActions.fetchLocationsWithPinnablesFailure, (state) => ({
		...state,
		locationsWithPinnablesLoading: false,
		locationsWithPinnablesLoaded: false,
	})),
	on(locationsActions.receiveLiveLocationUpdate, (state, { data }) => {
		if (!data) {
			return state;
		}
		const updatedLocationsWithPinnables = state.locationsWithPinnables.map((item) => {
			if (item.id === data.id) {
				return {
					...item,
					title: data.title,
					room: data.room,
					category: data.category,
					request_send_origin_teachers: data.request_send_origin_teachers,
					request_send_destination_teachers: data.request_send_destination_teachers,
					request_mode: data.request_mode,
					max_passes_to: data.max_passes_to,
					max_passes_to_active: data.max_passes_to_active,
					max_passes_from: data.max_passes_from,
					max_passes_from_active: data.max_passes_from_active,
				};
			}
			return item;
		});

		return {
			...state,
			locationsWithPinnables: updatedLocationsWithPinnables,
		};
	}),
	on(locationsActions.receiveLivePinnableUpdate, (state, { data }) => {
		if (!data) {
			return state;
		}
		const updatedLocationsWithPinnables = state.locationsWithPinnables.map((item) => {
			if ((data.type === 'location' && item.id === data.location.id) || (data.type === 'category' && item.category === data.category)) {
				return {
					...item,
					pinnable: {
						...item.pinnable,
						title: data.title,
						icon: data.icon,
						type: data.type,
						category: data.category,
						ignore_students_pass_limit: data.ignore_students_pass_limit,
						show_as_origin_room: data.show_as_origin_room,
						color_profile: data.color_profile,
					},
				};
			}
			return item;
		});

		return {
			...state,
			locationsWithPinnables: updatedLocationsWithPinnables,
		};
	})
);

export function locationsReducer(state: LocationsState | undefined, action: Action) {
	return reducer(state, action);
}
